import styles from "./FormSection.module.css";
import PropTypes, { node } from "prop-types";
import E3Button from "../InputControls/E3Button";
import { createSectionTableLayout } from "../Utils/sectionControlUtil";

import { getLocalStorageItem } from "e3-common/utils/localStorageUtil";
import { LOCAL_STORAGE_KEYS } from "e3-common/constants/enumerates";
import { existChk } from "e3-common/utils/controlsUtil";

export const createBtnArea = ({
  btnNodes,
  btnExcel,
  btnInit,
  btnAdd,
  btnSave,
  btnDel,
  additionalBtns,
  checkAuth,
  className,
}) => {
  const btnsJsx = [];
  const findBtnStyle = JSON.parse(
    getLocalStorageItem(LOCAL_STORAGE_KEYS.BUTTON_STYLE)
  );

  if (btnNodes && btnNodes.length > 0) {
    btnsJsx.push(...btnNodes);
  }

  if (btnInit && existChk(btnInit)) {
    btnsJsx.push(
      <E3Button
        {...(btnInit.ref && { ref: btnInit.ref })}
        key={"btnInit"}
        label={btnInit.label ?? "초기화"}
        onClick={btnInit.onClick}
        colorType={btnInit.colorType ?? findBtnStyle?.btnInit?.colorType}
        iconType={btnInit.iconType ?? findBtnStyle?.btnInit?.iconType}
        authBtn={btnInit.authBtn ?? "INIT"}
        checkAuth={checkAuth == null ? btnInit.checkAuth : checkAuth}
        disabled={btnInit.disabled}
      />
    );
  }
  if (btnExcel && existChk(btnExcel)) {
    btnsJsx.push(
      <E3Button
        {...(btnExcel.ref && { ref: btnExcel.ref })}
        key={"btnExcel"}
        label={btnExcel.label ?? "엑셀"}
        onClick={() => {
          btnExcel.onClick(btnExcel.name ?? null);
        }}
        colorType={
          btnExcel.colorType ?? findBtnStyle?.btnExcel?.colorType ?? "sub1"
        }
        iconType={
          btnExcel.iconType ?? findBtnStyle?.btnExcel?.iconType ?? "btn_down"
        }
        authBtn={btnExcel.authBtn ?? "EXCEL"}
        checkAuth={checkAuth === null ? btnExcel.checkAuth : checkAuth}
      />
    );
  }
  if (btnAdd && existChk(btnAdd)) {
    btnsJsx.push(
      <E3Button
        {...(btnAdd.ref && { ref: btnAdd.ref })}
        key={"btnAdd"}
        label={btnAdd.label ?? "추가"}
        onClick={btnAdd.onClick}
        colorType={
          btnAdd.colorType ?? findBtnStyle?.btnAdd?.colorType ?? "sub3"
        }
        iconType={btnAdd.iconType ?? findBtnStyle?.btnAdd?.iconType}
        authBtn={btnAdd.authBtn ?? "ADD"}
        checkAuth={checkAuth === null ? btnAdd.checkAuth : checkAuth}
        disabled={btnAdd.disabled}
      />
    );
  }
  if (btnSave && existChk(btnSave)) {
    btnsJsx.push(
      <E3Button
        {...(btnSave.ref && { ref: btnSave.ref })}
        key={"btnSave"}
        label={btnSave.label ?? "저장"}
        onClick={btnSave.onClick}
        colorType={
          btnSave.colorType ?? findBtnStyle?.btnSave?.colorType ?? "main"
        }
        iconType={btnSave.iconType ?? findBtnStyle?.btnSave?.iconType}
        authBtn={btnSave.authBtn ?? "SAVE"}
        checkAuth={checkAuth === null ? btnSave.checkAuth : checkAuth}
        disabled={btnSave.disabled}
      />
    );
  }
  if (btnDel && existChk(btnDel)) {
    btnsJsx.push(
      <E3Button
        {...(btnDel.ref && { ref: btnDel.ref })}
        key={"btnDel"}
        label={btnDel.label ?? "삭제"}
        onClick={btnDel.onClick}
        colorType={
          btnDel.colorType ?? findBtnStyle?.btnDel?.colorType ?? "sub3"
        }
        iconType={btnDel.iconType ?? findBtnStyle?.btnDel?.iconType}
        authBtn={btnDel.authBtn ?? "DELETE"}
        checkAuth={checkAuth === null ? btnDel.checkAuth : checkAuth}
        disabled={btnDel.disabled}
      />
    );
  }

  if (additionalBtns) btnsJsx.push(...additionalBtns);

  return (
    <div className={[styles.topBtnWrap, className].join(" ")}>{btnsJsx}</div>
  );
};

/**
 * 편집 Form 영역 Container
 * 자식 노드가 있는 경우, 자식 노드를 렌더링함 (일반적으로 <table> 태그로 시작)
 * 자식 노드가 없는 경우, formControls의 형태에 따라 렌더링
 * @returns
 */
function FormSection({
  children,
  formControls,
  numOfCols,
  btnNodes,
  btnExcel,
  btnInit,
  btnAdd,
  btnSave,
  btnDel,
  checkAuth,
  additionalBtns,
  formOptions,
  btnArea,
  clearTopBorder,
  overflowIsUnSet,
}) {
  if (children) {
    return (
      <>
        <div className={styles.formSection}>{children}</div>
        {createBtnArea({
          btnNodes,
          btnExcel,
          btnInit,
          btnAdd,
          btnSave,
          btnDel,
          additionalBtns,
          checkAuth,
        })}
      </>
    );
  }

  const rowsJsx = createSectionTableLayout(
    formControls,
    numOfCols,
    formOptions
  );

  const trsJsx = rowsJsx?.map((tds, index) => <tr key={index}>{tds}</tr>);
  const additionalBtnsArry = createBtnArea({
    btnNodes,
    btnExcel,
    btnInit,
    btnAdd,
    btnSave,
    btnDel,
    additionalBtns,
    checkAuth,
  });
  return (
    <>
      {btnArea == "up" && !clearTopBorder && additionalBtnsArry}
      <div
        className={[
          styles.formSection,
          clearTopBorder ? styles.clearTopBorder : "",
        ].join(" ")}
      >
        <table>
          <tbody>{trsJsx}</tbody>
        </table>
      </div>
      {btnArea == "down" && !clearTopBorder && additionalBtnsArry}
    </>
  );
}

FormSection.defaultProps = {
  numOfCols: 4,
  checkAuth: false,
  btnArea: "up",
  clearTopBorder: false,
};

FormSection.propTypes = {
  children: PropTypes.node,
  formControls: PropTypes.object,
  numOfCols: PropTypes.number,
  btnNodes: PropTypes.arrayOf(node),
  btnAdd: PropTypes.object,
  btnInit: PropTypes.object,
  btnSave: PropTypes.object,
  btnDel: PropTypes.object,
  checkAuth: PropTypes.bool,
  additionalBtns: PropTypes.arrayOf(node),
  btnArea: PropTypes.string,
  clearTopBorder: PropTypes.bool,
};

export default FormSection;
